import React from "react";
import { elakayAutoText } from "./dataElakay";
import { elakayMoment } from "../../utils/elakayDate";
import {
    FaExclamationTriangle, FaClipboardCheck, FaBoxOpen, FaTruck,
    FaWalking, FaHandHolding, FaUndo, FaCheck,
} from "react-icons/fa";

// Order Tracking Steps -- vertical timeline for the Tracking Number modal's
// "Order Progress" view (Order Tracking Display task spec §6). Purely
// presentational: every `step.state` (completed/current/pending) and
// `step.date` already comes normalized from the server
// (elakay_services/shipping/trackingSteps.js via GET
// /shipping/tracking/order/:orderId/:orderProductId) -- this component
// contains no provider-specific or status-mapping logic (task spec §8).
//
// One icon per canonical step (decorative only -- keyed off the fixed
// `step.key` the server always sends, never off a provider status), so
// each stage reads at a glance instead of a bare checkmark/dot.
const STEP_ICON = {
    confirmed: FaClipboardCheck,
    packed: FaBoxOpen,
    shipped: FaTruck,
    out_for_delivery: FaWalking,
    delivered: FaHandHolding,
    returned: FaUndo,
};

// Step names and exception reasons in the active language, keyed off the fixed values the server
// sends (step.key / exception.reason); the server's English label stays the fallback.
const STEP_LABEL = {
    confirmed: ["h_trk_step_confirmed", "Order Confirmed"],
    packed: ["h_trk_step_packed", "Order Packed"],
    shipped: ["h_trk_step_shipped", "Package Shipped"],
    out_for_delivery: ["h_trk_step_out_for_delivery", "Out For Delivery"],
    delivered: ["h_trk_step_delivered", "Order Delivered"],
    returned: ["h_trk_step_returned", "Return"],
};
const EXCEPTION_REASON = {
    exception: ["h_trk_reason_exception", "delivery exception"],
    held: ["h_trk_reason_held", "held"],
    cancelled: ["h_trk_reason_cancelled", "cancelled"],
};
const stepLabel = (step) => (STEP_LABEL[step.key] ? elakayAutoText(STEP_LABEL[step.key][0], step.label || STEP_LABEL[step.key][1]) : step.label);
const exceptionReason = (reason) => (EXCEPTION_REASON[reason] ? elakayAutoText(...EXCEPTION_REASON[reason]) : reason);

const formatStepDate = (isoDate) => (
    isoDate ? elakayMoment(isoDate).format("llll") : null
);

const TrackingSteps = ({ steps, exception, justChangedKeys, infoSource }) => {
    if (!Array.isArray(steps) || steps.length === 0) {
        return (
            <div className="tracking-steps-empty">
                {elakayAutoText("h_trk_unavailable", "Tracking information is not available yet.")}
            </div>
        );
    }

    // "Return" only earns its own visible row once it's actually reached or
    // in progress -- otherwise it stays in the list but visually muted,
    // matching how the reference timeline treats it as the tail of the
    // happy path rather than a guaranteed step (task spec §6).
    return (
        <div className="tracking-steps">
            {exception?.active && (
                <div className="tracking-steps-exception">
                    <FaExclamationTriangle className="tracking-steps-exception-icon" aria-hidden="true" />
                    <span>
                        {elakayAutoText("h_trk_exception", "There's an issue with this shipment.")}
                        {exception.reason ? ` (${exceptionReason(exception.reason)})` : ""}
                    </span>
                </div>
            )}
            <ul className="tracking-steps-list">
                {steps.map((step, index) => {
                    const isLast = index === steps.length - 1;
                    const formattedDate = formatStepDate(step.date);
                    const StepIcon = STEP_ICON[step.key] || FaCheck;
                    // A small, one-shot "just progressed" animation (task
                    // spec §9/§30) -- only for a step OrdersTrackingModal
                    // detected as genuinely new since this browser last
                    // saw this order's tracking, never on every render.
                    const justChanged = justChangedKeys?.has?.(step.key);
                    return (
                        <li
                            key={step.key}
                            className={`tracking-step tracking-step--${step.state} ${justChanged ? "tracking-step--just-changed" : ""}`}
                        >
                            <div className="tracking-step-marker">
                                <span className="tracking-step-icon" aria-hidden="true">
                                    <StepIcon />
                                    {step.state === "completed" && (
                                        <span className="tracking-step-icon-badge">
                                            <FaCheck />
                                        </span>
                                    )}
                                </span>
                                {!isLast && <span className="tracking-step-connector" />}
                            </div>
                            <div className="tracking-step-content">
                                <span className="tracking-step-label">{stepLabel(step)}</span>
                                {formattedDate && (
                                    <span className="tracking-step-date">{formattedDate}</span>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default TrackingSteps;
